import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  DiagramacaoPreviewProps,
  PageConfig,
} from "./types/types";
import { usePageEditor } from "./hooks/usePageEditor";

// Importar os estilos
import "./DiagramacaoPreview.css";

// Import component parts
import { Toolbar } from "./parts/Toolbar";
import { CoverPage } from "./parts/CoverPage";
import { QuestionPages } from "./parts/QuestionPages";
import { EditorModal } from "./parts/EditorModal";
import { PageEditorModal } from "./parts/PageEditorModal";
import { Loading } from "../../../../../component/common/Loading";
import { DownloadModal } from "../../../../../component/common/DownloadModal";


// Importar as funções do utilitário
import { 
  calculatePages, 
  recalculateFromPages, 
  createPageCalculatorConfig 
} from './utils/pageCalculator';

// ──────────────────────────────────────────────
// CONSTANTES (A4 e layout)
// ──────────────────────────────────────────────

const A4_WIDTH = 792;
const A4_HEIGHT = 1119.5;
//const COLUMN_GAP = 20;
const QUESTION_MARGIN = 15;

// ──────────────────────────────────────────────
// INTERFACE PARA CONFIGURAÇÃO DO CALCULADOR DE PÁGINAS
// ──────────────────────────────────────────────

export interface PageCalculatorConfig {
  A4_WIDTH: number;
  A4_HEIGHT: number;
  numeroColunas: number;
  fontSizeQuestoes: number | "default";
  fontSizeAlternatives: number | "default";
  marginSuperior: number;
  marginInferior: number;
  marginEsquerda: number;
  marginDireita: number;
  randomAlternatives: boolean;
  randomQuestion: boolean;
  testId?: string;
  diagramId?: string;
  answerSheetId?: string;
  registrationId?: string;
  studentId?: string;
  edit?: boolean;
  urltoken?: string;
  editMode?: boolean;
}

// ──────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ──────────────────────────────────────────────

const DiagramacaoPreview: React.FC<DiagramacaoPreviewProps> = (props) => {
  const {
    provaSelect,
    numeroColunas,
    fontSizeQuestoes,
    marginSuperior,
    marginInferior,
    marginEsquerda,
    marginDireita,
    loading,
    questionPages,
    setQuestionPages,
    setLoading,
    randomAlternatives,
    randomQuestion,
    DataFrontPage,
    setDataFrontPage,
    fontSizeAlternatives,
    borderVisible,
    editMode,
    printMode,
    testId,
    diagramId,
    answerSheetId,
    registrationId,
    studentId,
    urltoken,
  } = props;

  const [selectedElement, setSelectedElement] = useState<number | null>(null);
  const [showEditor, setShowEditor] = useState<boolean>(false);
  const [tempContent, setTempContent] = useState<string>("");
  const [pageColumns, setPageColumns] = useState<number[]>([]);
  const [showPageEditor, setShowPageEditor] = useState<boolean>(false);
  const [pageEditorContent, setPageEditorContent] = useState<string>("");
  const [pageBeingEdited, setPageBeingEdited] = useState<number | null>(null);
  const [pageEditorColumns, setPageEditorColumns] =
    useState<number>(numeroColunas);
  const [pageConfigs, setPageConfigs] = useState<Record<number, PageConfig>>(
    {}
  );
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [needsRecalc, setNeedsRecalc] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const editorRef = useRef(null);
  const printRef = useRef<HTMLDivElement | null>(null);
    // Estado para indicar se está em polling e para armazenar o link para download
  const [isPolling, setIsPolling] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const dissertiveMode = provaSelect?.type === "dissertative"; 

  

  /**
   * Recalcula todas as páginas da prova a partir das questões selecionadas
   */
  function recalcPages() {
    // Verifica se há questões selecionadas
    if (!provaSelect?.questions || !provaSelect.questions.length) {
      setQuestionPages([]);
      return;
    }

    setLoading(true);

    // Cria a configuração para o cálculo das páginas
    const config = createPageCalculatorConfig({
      A4_WIDTH,
      A4_HEIGHT,
      numeroColunas,
      fontSizeQuestoes,
      fontSizeAlternatives,
      marginSuperior,
      marginInferior,
      marginEsquerda,
      marginDireita,
      randomAlternatives,
      randomQuestion,
      testId,
      diagramId,
      answerSheetId,
      registrationId,
      studentId,
      urltoken,
      editMode: editMode || false,
    });

    try {
      // Verifica se está em modo de edição e há páginas existentes
      if (editMode && questionPages && questionPages.length > 0) {
        
        // Usa recalculateFromPages para preservar a estrutura das páginas existentes
        const pagesForRecalc = JSON.parse(JSON.stringify(questionPages));
        
        const updatedPages = recalculateFromPages({
          questionPages: pagesForRecalc,
          config,
          pageConfigs,
          pageColumns,
          setLoading: (loading) => setLoading(loading),
        });
        
        setQuestionPages([...updatedPages]);
      } else {
        const pages = calculatePages({
          provaSelect: provaSelect.questions,
          config,
          pageConfigs,
          pageColumns,
          setLoading: (loading) => setLoading(loading),
        });

        setQuestionPages(pages);
      }
    } catch (error) {
      console.error("Erro ao recalcular páginas:", error);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Recalcula as páginas a partir das páginas de questões existentes
   * Útil para quando apenas o estilo ou layout é alterado, sem mudar o conteúdo
   */
  const recalcPagesEditAsync = useCallback(() => {
    // Verifica se há páginas de questões
    if (!questionPages || questionPages.length === 0) {
      console.warn("Não há páginas para recalcular");
      return;
    }

    setLoading(true);

    try {
      // Cria a configuração para o cálculo das páginas - SEMPRE em modo de edição
      const config = createPageCalculatorConfig({
        A4_WIDTH,
        A4_HEIGHT,
        numeroColunas,
        fontSizeQuestoes,
        fontSizeAlternatives,
        marginSuperior,
        marginInferior,
        marginEsquerda,
        marginDireita,
        randomAlternatives,
        randomQuestion,
        testId,
        diagramId,
        answerSheetId,
        registrationId,
        studentId,
        urltoken,
        editMode: editMode || false,
      });

      // Força uma cópia profunda das páginas de questões para evitar problemas de referência
      const pagesForRecalc = JSON.parse(JSON.stringify(questionPages));

      let updatedPages;


      updatedPages = recalculateFromPages({
        questionPages: pagesForRecalc,
        config, // Passando o config com editMode = true
        pageConfigs,
        pageColumns,
        setLoading: (loading) => setLoading(loading),
      });


      setQuestionPages([...updatedPages]);
    } catch (error) {
      console.error("Erro ao recalcular estilos das páginas:", error);
    } finally {
      setLoading(false);
    }
  }, [
    questionPages,
    setLoading,
    numeroColunas,
    fontSizeQuestoes,
    fontSizeAlternatives,
    marginSuperior,
    marginInferior,
    marginEsquerda,
    marginDireita,
    randomAlternatives,
    randomQuestion,
    testId,
    diagramId,
    answerSheetId,
    registrationId,
    studentId,
    urltoken,
    pageConfigs,
    pageColumns,
    setQuestionPages,
    editMode
  ]);

  const {
    handleAddElement,
    handleColumnChange,
    handleEditElement,
    handleEditorChange,
    handleConfirmEdit,
    handleCancelEdit,
    handleRemoveElement,
    handleDragStopCover,
    handleResizeCover,
    handleInlineEdit,
    handleEditPage,
    handleConfirmEditPage,
    handleCancelEditPage,
  } = usePageEditor(
    pageBeingEdited,
    setPageBeingEdited,
    pageEditorContent,
    setPageEditorContent,
    pageEditorColumns,
    setPageEditorColumns,
    showPageEditor,
    setShowPageEditor,
    pageConfigs,
    setPageConfigs,
    selectedElement ?? 0,
    setSelectedElement,
    tempContent,
    setTempContent,
    showEditor,
    setShowEditor,
    DataFrontPage,
    setDataFrontPage,
    questionPages,
    setQuestionPages,
    pageColumns,
    setPageColumns,
    numeroColunas,
    fontSizeQuestoes,
    QUESTION_MARGIN,
    calculatePages,
    recalculateFromPages,
    !!recalcPagesEditAsync,
    needsRecalc,
    setNeedsRecalc,
    editMode,
    printMode,
    isEditing,
    setIsEditing
  );

  // Função auxiliar para extrair o tipo de elemento
  const getElementType = (html: string): string => {
    const dataTypeMatch = html.match(/data-element-type=["']([^"']+)["']/i);
    return dataTypeMatch ? dataTypeMatch[1] : "question";
  };

  const storedQuestionPages = useRef(questionPages);
  useEffect(() => {
    storedQuestionPages.current = questionPages;
  }, [questionPages]);

  useEffect(() => {

    if (editMode === true || printMode === true) {
      recalcPagesEditAsync();
    } else {
      recalcPages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    provaSelect,
    numeroColunas,
    pageColumns,
    fontSizeQuestoes,
    fontSizeAlternatives,
    marginSuperior,
    marginInferior,
    marginEsquerda,
    marginDireita,
    randomAlternatives,
    randomQuestion,
    DataFrontPage,
  ]);

  useEffect(() => {
    const newColumns = questionPages.map(() => numeroColunas);
    setPageColumns((prevColumns) => {
      // Compara se o array anterior é igual ao novo
      if (
        prevColumns.length === newColumns.length &&
        prevColumns.every((value, index) => value === newColumns[index])
      ) {
        return prevColumns;
      }
      return newColumns;
    });
  }, [numeroColunas, questionPages]);

  useEffect(() => {
    if (needsRecalc) {
      recalcPagesEditAsync();
      setNeedsRecalc(false);
    }
  }, [questionPages, needsRecalc, recalcPagesEditAsync]);

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center vh-100"
        style={{ background: "#f8f9fa" }}
        data-testid="loading-container"
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  // Add CSS class when printing
  const printContainerClass = isPrinting ? "printing" : "";

  return (
    <div
      className={`container-fluid py-4 ${isPrinting ? "printing-mode" : ""}`}
      style={{ background: "#f8f9fa" }}
      id="diagramacao-preview"
      data-testid="diagramacao-preview-container"
    >
      {/* TOOLBAR - Only visible when not printing */}
      {!isPrinting && (
        <Toolbar
          selectedElement={selectedElement}
          handleAddElement={handleAddElement}
          handleEditElement={handleEditElement}
          handleRemoveElement={handleRemoveElement}
          recalcPages={recalcPages}
          printMode={printMode}
          downloadUrl={downloadUrl}
          setIsPrinting={setIsPrinting}
          setDownloadUrl={setDownloadUrl}
          setIsPolling={setIsPolling}
          isPolling={isPolling}
          testId={testId}
          diagramId={diagramId}
          data-testid="toolbar"
        />
      )}

      {/* Renderiza o Loading via portal enquanto o polling estiver ativo */}
      {isPolling && (
        <Loading
          message="Gerando diagrama, por favor aguarde..."
          id="loading-polling"
          data-testid="loading-polling"
        />
      )}

      {/* Renderiza o modal de download via portal se o link estiver disponível */}
      {downloadUrl && (
        <DownloadModal
          message="Diagrama gerado com sucesso! Clique no botão para baixar o ZIP."
          downloadUrl={downloadUrl}
          onClose={() => setDownloadUrl(null)}
          data-testid="download-modal"
        />
      )}

      {/* MODAL: Edição de elementos da capa - Only visible when not printing */}
      {showEditor && selectedElement !== null && !isPrinting && (
        <EditorModal
          editorRef={editorRef}
          tempContent={tempContent}
          handleEditorChange={handleEditorChange}
          handleCancelEdit={handleCancelEdit}
          handleConfirmEdit={handleConfirmEdit}
          data-testid="editor-modal"
        />
      )}

      {/* MODAL: Edição de páginas (questões) - Only visible when not printing */}
      {showPageEditor && pageBeingEdited !== null && !isPrinting && (
        <PageEditorModal
          editorRef={editorRef}
          pageBeingEdited={pageBeingEdited}
          pageEditorContent={pageEditorContent}
          pageEditorColumns={pageEditorColumns}
          handleColumnChange={handleColumnChange}
          setPageEditorContent={setPageEditorContent}
          handleCancelEditPage={handleCancelEditPage}
          handleConfirmEditPage={handleConfirmEditPage}
          dissertiveMode={dissertiveMode}
          data-testid="page-editor-modal"
        />
      )}

      {/* CONTEÚDO A SER IMPRESSO */}
      <div ref={printRef} className={`print_preview ${printContainerClass}`} data-testid="print-preview-container">
        {/* CAPA */}
        <CoverPage
          DataFrontPage={DataFrontPage}
          A4_WIDTH={A4_WIDTH}
          A4_HEIGHT={A4_HEIGHT}
          marginSuperior={marginSuperior}
          marginInferior={marginInferior}
          marginEsquerda={marginEsquerda}
          marginDireita={marginDireita}
          selectedElement={isPrinting ? null : selectedElement}
          setSelectedElement={setSelectedElement}
          handleDragStopCover={handleDragStopCover}
          handleResizeCover={handleResizeCover}
          data-testid="cover-page"
        />

        {/* PÁGINAS DE QUESTÕES */}
        <QuestionPages
          questionPages={questionPages}
          A4_WIDTH={A4_WIDTH}
          A4_HEIGHT={A4_HEIGHT}
          marginSuperior={marginSuperior}
          marginInferior={marginInferior}
          marginEsquerda={marginEsquerda}
          marginDireita={marginDireita}
          borderVisible={borderVisible}
          numeroColunas={numeroColunas}
          fontSizeQuestoes={fontSizeQuestoes}
          fontSizeAlternatives={fontSizeAlternatives}
          handleEditPage={handleEditPage}
          handleInlineEdit={handleInlineEdit}
          getElementType={getElementType}
          testId={testId}
          diagramId={diagramId}
          answerSheetId={answerSheetId}
          registrationId={registrationId}
          studentId={studentId}
          urltoken={urltoken}
          dissettiveMode={dissertiveMode}
          data-testid="question-pages"
        />
      </div>
    </div>
  );
};

export default DiagramacaoPreview;